import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";

import type {
  Section12AdminPaymentStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";

import { formatDoctorDisplayName } from "../Section12PaymentRequests/payment-request-helpers.js";
import { api } from "~/api.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

interface UpdatePaymentStatusDialogProps {
  request: Section12PaymentRequestWithDetails | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ADMIN_STATUS_OPTIONS: Array<{
  value: Section12AdminPaymentStatus;
  label: string;
}> = [
  { value: "accepted", label: "Approved by AMHP" },
  { value: "paid", label: "Paid" },
  { value: "payment-processing", label: "Payment Processing" },
  { value: "payment-on-hold", label: "Payment on hold" },
  { value: "receipt-missing", label: "Receipt Missing" },
  { value: "bank-information-missing", label: "Bank Information Missing" },
  { value: "value-incorrect", label: "Value Incorrect" },
  {
    value: "registration-with-icb-needed",
    label: "Registration with ICB needed",
  },
];

export function UpdatePaymentStatusDialog({
  request,
  onClose,
  onSuccess,
}: UpdatePaymentStatusDialogProps) {
  const [selectedStatus, setSelectedStatus] =
    useState<Section12AdminPaymentStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedStatus(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!request || !selectedStatus) return;

    setIsSubmitting(true);
    try {
      await api.section12.adminUpdatePaymentStatus(request.id, {
        status: selectedStatus,
      });
      renderSuccessToast({
        message: "Payment status updated successfully",
      });
      setSelectedStatus(null);
      onSuccess();
    } catch (error) {
      console.error("Failed to update payment status:", error);
      renderErrorToast({
        message: "Failed to update payment status. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out the current status to prevent no-op updates
  const availableOptions = ADMIN_STATUS_OPTIONS.filter(
    (option) => option.value !== request?.status,
  );

  return (
    <Dialog
      open={request !== null}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Update Payment Status</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select the new payment status for{" "}
          <strong>
            {request?.doctor
              ? formatDoctorDisplayName(
                  request.doctor.name,
                  request.doctor.teamName,
                  request.doctor.teamType,
                )
              : "this doctor"}
          </strong>
          {"'s"} payment request.
        </DialogContentText>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Payment Status</FormLabel>
          <RadioGroup
            value={selectedStatus ?? ""}
            onChange={(e) => {
              setSelectedStatus(e.target.value as Section12AdminPaymentStatus);
            }}
          >
            {availableOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          variant="contained"
          disabled={!selectedStatus || isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
